import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { Typography } from '~/components/elements/Typography'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { exists } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useAddCoreMemberMutation } from '../hooks/useAddCoreMemberMutation'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useMainQueryCoreMembers } from '../hooks/useMainQuery'

export const AddCoreMemberDialog = () => {
  const { t } = useTranslation()
  const coreMembers = useMainQueryCoreMembers()
  const schema = z.object({
    email: z
      .string()
      .email()
      .refine(
        (email) => !coreMembers.some((member) => member.email === email),
        { message: t('this_email_is_already_registered') },
      ),
  })
  type InputValues = z.infer<typeof schema>
  const resolver = zodResolver(schema)

  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: { email: '' },
    resolver,
    mode: 'onChange',
  })
  const emailField = useFormField(control, 'email')

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const notify = useNotify()
  const logEvent = useLogEvent()

  const addCoreMemberMutation = useAddCoreMemberMutation({
    onSuccess: (response, email) => {
      if (exists(response.addCoreMember)) {
        notify({ content: t('mforce_administrator_added'), style: 'success' })

        // addCoreMember が null のときはユーザーテーブルに存在せず追加されていないため、記録しない
        logEvent('Add core member', {
          eventProperties: [{ key: 'email', value: email }],
        })
      } else {
        notify({
          content: t(
            'the_specified_email_address_does_not_exist_in_the_user_table',
          ),
          style: 'error',
        })
      }
      closeDialogWithRefresh()
    },
  })
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ email }) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      addCoreMemberMutation.mutate(email)
    },
    [addCoreMemberMutation, isDirty, resetDialogInfo],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={addCoreMemberMutation.isPending}
      mode="create"
      title={t('add_mforce_administrator')}
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <LabelingContainer label={t('email_address')}>
        <StyledInput autoFocus={true} width="100%" {...emailField} />

        <ErrorMessage
          errors={errors}
          name="email"
          render={({ message }) => (
            <Typography color="error" variant="normal">
              {message}
            </Typography>
          )}
        />
      </LabelingContainer>
    </ExecuteCancelDialog>
  )
}
